import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronDown } from 'lucide-react';

import { API_URL } from '../config';
import ModalShell from './ModalShell';
import useToast from '../hooks/useToast';
import { haptic } from '../utils/vibration';
import { formatTimezoneDate } from '../utils/timezone';
import { safeFetch } from '../utils/safeFetch';

const SERVICE_TYPE_LABELS = {
  washing: 'Стирка',
  cleaning: 'Химчистка мебели',
  extra_order: 'Дополнительный заказ',
};

const STAGE_LABELS = {
  new: 'Новый',
  pickup: 'Доставка от клиента',
  washing: 'Стирка',
  washing_laundry: 'Прачечная',
  washing_cleaning: 'Чистка',
  cleaning: 'Химчистка мебели',
  drying_packing: 'Сушка и упаковка',
  approval: 'Согласование',
  delivery: 'Доставка клиенту',
  handover: 'Выдача',
  completed: 'Завершён',
  cancelled: 'Отменён',
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  return `${amount.toLocaleString('ru-RU', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  })} ₽`;
};

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getItemNumber = (order, item, index) => {
  const raw = String(item?.item_number || '').trim();
  if (raw) return raw;
  return `${order?.order_number || order?.id || 'Заказ'}-${item?.sort_order || index + 1}`;
};

const formatPhoneDisplay = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  const normalized = digits.length === 11 && digits.startsWith('8')
    ? `7${digits.slice(1)}`
    : digits;
  if (normalized.length !== 11 || !normalized.startsWith('7')) {
    return value || '-';
  }
  return `+7(${normalized.slice(1, 4)})-${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`;
};

const getPickupTypeLabel = (order) => {
  if (!order) return '-';
  if (order.service_type === 'cleaning') return 'Визит специалиста';
  if (order.self_delivery) return 'Самостоятельная доставка';
  return 'Доставка от клиента';
};

const getClientLabel = (order) => {
  if (!order) return 'Клиент не указан';
  if (String(order.client_type || '').toLowerCase() === 'b2b') {
    return order.client_name || 'Юридическое лицо';
  }
  return order.client_name || 'Физическое лицо';
};

const getItemMetaLabel = (item) => {
  if (!item) return '1 шт.';
  if ((item.unit === 'м2' || item.unit === 'м²') && item.area) {
    return `${item.area} м²`;
  }
  if (item.unit === 'кг' && item.weight) {
    return `${item.weight} кг`;
  }
  return `${Number(item.quantity || 1)} ${item.unit || 'шт'}`;
};

const getStageLabel = (status) => STAGE_LABELS[String(status || '').trim().toLowerCase()] || status || '-';

const buildBasketSummary = (mainItems, orderServices, discountItems) => {
  const parts = [`${mainItems.length} предм.`];
  if (orderServices.length > 0) parts.push(`${orderServices.length} усл.`);
  if (discountItems.length > 0) parts.push(`${discountItems.length} скид.`);
  return parts.join(' • ');
};

const getSummaryTitle = (order, orderNumber, orderId) => `Сводка заказа №${order?.order_number || orderNumber || orderId || '—'}`;

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-right font-medium text-gray-900 dark:text-gray-100">{value || '-'}</span>
  </div>
);

const OrderCreatedSummaryModal = ({
  open,
  orderId,
  orderNumber,
  onClose,
  variant = 'modal',
  backAriaLabel = 'Вернуться назад',
  onOrderLoaded,
}) => {
  const [order, setOrder] = useState(null);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showItems, setShowItems] = useState(true);

  const loadOrderSummary = useCallback(async () => {
    if (!orderId) return null;

    setLoading(true);
    setError('');
    setOrder(null);

    try {
      const data = await safeFetch(`${API_URL}/api/orders/${orderId}`);
      setOrder(data);
      onOrderLoaded?.(data);
      return data;
    } catch (err) {
      setOrder(null);
      const msg = err.message || 'Не удалось загрузить сводку заказа';
      setError(msg);
      showToast(msg, 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [onOrderLoaded, orderId]);

  useEffect(() => {
    if (!open || !orderId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      setOrder(null);

      try {
        const data = await safeFetch(`${API_URL}/api/orders/${orderId}`);
        if (!cancelled) {
          setOrder(data);
          onOrderLoaded?.(data);
        }
      } catch (err) {
        if (!cancelled) {
          setOrder(null);
          const msg = err.message || 'Не удалось загрузить сводку заказа';
          setError(msg);
          showToast(msg, 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, orderId, onOrderLoaded]);

  const mainItems = useMemo(
    () => (order?.items || []).filter((item) => !item?.is_additional),
    [order]
  );
  const additionalItems = useMemo(
    () => (order?.items || []).filter((item) => item?.is_additional),
    [order]
  );
  const orderServices = useMemo(
    () => additionalItems.filter((item) => !item?.discount_id && String(item?.additional_type || '').toLowerCase() !== 'discount'),
    [additionalItems]
  );
  const discountItems = useMemo(
    () => additionalItems.filter((item) => item?.discount_id || String(item?.additional_type || '').toLowerCase() === 'discount'),
    [additionalItems]
  );
  const mainItemsTotal = useMemo(
    () => mainItems.reduce((sum, item) => sum + Number(item?.subtotal ?? item?.price ?? 0), 0),
    [mainItems]
  );
  const orderServicesTotal = useMemo(
    () => orderServices.reduce((sum, item) => sum + Number(item?.subtotal ?? item?.price ?? 0), 0),
    [orderServices]
  );
  const discountTotal = useMemo(
    () => discountItems.reduce((sum, item) => sum + Math.abs(Number(item?.subtotal ?? item?.price ?? 0)), 0),
    [discountItems]
  );
  const finalTotal = Number(order?.final_amount ?? (mainItemsTotal + orderServicesTotal - discountTotal) ?? 0);
  const pickupDateLabel = order?.pickup_date ? formatTimezoneDate(order.pickup_date, false) : '';
  const pickupTimeLabel = String(order?.pickup_time_slot || '').trim();
  const shouldShowSchedule = !order?.self_delivery && !!(pickupDateLabel || pickupTimeLabel);
  const basketSummary = buildBasketSummary(mainItems, orderServices, discountItems);
  const isPageVariant = variant === 'page';
  const createdAtLabel = order?.created_date ? formatTimezoneDate(order.created_date, true) : 'Не указана';
  const statusLabel = order?.lifecycle_label || getStageLabel(order?.lifecycle_status) || 'Не указан';
  const stageLabel = getStageLabel(order?.status);

  const cardContent = (
    <div className="w-full overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
      {!isPageVariant ? (
        <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label={backAriaLabel}
          >
            <ArrowLeft className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
          <div className="min-w-0">
            <div className="truncate text-base font-bold text-gray-900 dark:text-gray-100">
              {getSummaryTitle(order, orderNumber, orderId)}
            </div>
          </div>
        </div>
      ) : null}

      <div className={`${isPageVariant ? 'p-4' : 'max-h-[calc(90vh-72px)] overflow-y-auto p-4'} text-sm`}>
        {loading ? (
          <div className="py-10 text-center text-gray-500 dark:text-gray-400">
            Загружаю сводку заказа...
          </div>
        ) : error ? (
          <div className="space-y-3">
            <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
            <button
              type="button"
              onClick={loadOrderSummary}
              className="w-full rounded-xl bg-blue-600 px-4 py-2.5 font-medium text-white transition-colors hover:bg-blue-700"
            >
              Повторить
            </button>
          </div>
        ) : order ? (
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
                  Статус: {statusLabel}
                </span>
                <span className="inline-flex items-center rounded-full bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700 dark:bg-violet-950/30 dark:text-violet-300">
                  Этап: {stageLabel}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Создан: {createdAtLabel}
              </div>
            </div>

            <div className="space-y-2">
              <Row label="Клиент" value={getClientLabel(order)} />
              <Row label="Телефон" value={formatPhoneDisplay(order.client_phone)} />
              {order.pickup_address_text ? <Row label="Адрес" value={order.pickup_address_text} /> : null}
              <Row label="Услуга" value={SERVICE_TYPE_LABELS[order.service_type] || 'Заказ'} />
              <Row label="Тип забора" value={getPickupTypeLabel(order)} />
              {shouldShowSchedule ? <Row label="Дата" value={pickupDateLabel} /> : null}
              {shouldShowSchedule && pickupTimeLabel ? <Row label="Время" value={pickupTimeLabel} /> : null}
              {order.department_name ? <Row label="Подразделение" value={order.department_name} /> : null}
            </div>

            <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setShowItems((value) => !value)}
                className="flex w-full items-center justify-between gap-3"
              >
                <div className="min-w-0 text-left">
                  <div className="font-semibold text-gray-900 dark:text-gray-100">Корзина</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{basketSummary}</div>
                </div>
                <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${showItems ? 'rotate-180' : ''}`} />
              </button>

              {showItems ? (
                <div className="mt-3 space-y-2">
                  {mainItems.map((item, index) => {
                    const attachedServices = parseJsonArray(item.additional_services_snapshot);
                    const itemNumber = getItemNumber(order, item, index);
                    return (
                      <div key={item.id} className="rounded-xl bg-blue-50 px-3 py-2.5 dark:bg-blue-950/20">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-col gap-0.5">
                              <span className="shrink-0 text-[11px] font-mono text-blue-600 dark:text-blue-300">{itemNumber}</span>
                              <span className="truncate font-medium text-gray-900 dark:text-gray-100">{item.item_name}</span>
                            </div>
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{getItemMetaLabel(item)}</div>
                            {attachedServices.length > 0 ? (
                              <div className="mt-1 text-xs text-purple-600 dark:text-purple-300">
                                Доп. услуги: {attachedServices.map((service) => service?.name || 'Без названия').join(', ')}
                              </div>
                            ) : null}
                          </div>
                          <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-300">
                            {formatMoney(item.subtotal ?? item.price)}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {orderServices.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-3 rounded-xl bg-purple-50 px-3 py-2.5 dark:bg-purple-950/20">
                      <span className="truncate text-gray-900 dark:text-gray-100">{item.item_name}</span>
                      <span className="shrink-0 text-xs font-medium text-purple-600 dark:text-purple-300">
                        +{formatMoney(item.subtotal ?? item.price)}
                      </span>
                    </div>
                  ))}

                  {discountItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-3 rounded-xl bg-orange-50 px-3 py-2.5 dark:bg-orange-950/20">
                      <span className="truncate text-gray-900 dark:text-gray-100">{item.item_name}</span>
                      <span className="shrink-0 text-xs font-medium text-orange-600 dark:text-orange-300">
                        -{formatMoney(Math.abs(Number(item.subtotal ?? item.price ?? 0)))}
                      </span>
                    </div>
                  ))}

                  {mainItems.length === 0 && orderServices.length === 0 && discountItems.length === 0 ? (
                    <div className="py-2 text-center text-xs text-gray-400 dark:text-gray-500">Корзина пуста</div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {order.notes ? (
              <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">Комментарий</div>
                <div className="mt-1 whitespace-pre-line text-gray-900 dark:text-gray-100">{order.notes}</div>
              </div>
            ) : null}

            {order.internal_notes ? (
              <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">Доп. информация</div>
                <div className="mt-1 whitespace-pre-line text-gray-900 dark:text-gray-100">{order.internal_notes}</div>
              </div>
            ) : null}

            <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
              <div className="space-y-2">
                <Row label="Предметы" value={formatMoney(mainItemsTotal)} />
                {orderServicesTotal > 0 ? <Row label="Доп. услуги" value={`+${formatMoney(orderServicesTotal)}`} /> : null}
                {discountTotal > 0 ? <Row label="Скидка" value={`-${formatMoney(discountTotal)}`} /> : null}
                <div className="flex items-start justify-between gap-3 border-t border-gray-200 pt-2 text-base font-semibold dark:border-gray-700">
                  <span className="text-gray-900 dark:text-gray-100">К оплате</span>
                  <span className="text-gray-900 dark:text-gray-100">{formatMoney(finalTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  if (isPageVariant) {
    return (
      <div className="min-h-full bg-slate-100 px-4 py-4 dark:bg-slate-950 sm:px-6 sm:py-6">
        <div className="mx-auto max-w-md">
          {cardContent}
        </div>
      </div>
    );
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="sheet"
      closeOnOverlay={false}
      panelClassName="max-h-[90vh] max-w-md overflow-hidden p-0"
    >
      {cardContent}
    </ModalShell>
  );
};

export default OrderCreatedSummaryModal;
